// ===========================================
// BUSTED! BLACKJACK - CANVAS GAME TABLE
// ===========================================
// Canvas-based game table rendering for single-system
// visual control. All game elements (cards, chips, seats)
// are drawn to a single canvas element.
// ===========================================

import { useEffect, useRef, useCallback, useState, useImperativeHandle, forwardRef } from 'react';
import type { Card as CardType, DealerState } from '@busted-blackjack/shared';

// ===========================================
// ASSET IMPORTS
// ===========================================

// Table background
import tableImage from '../../../assets/table2.png';

// Card assets
import cardBack from '../../../assets/cards/card_back.svg';
import jackHearts from '../../../assets/cards/jack-hearts.svg';
import jackDiamonds from '../../../assets/cards/jack-diamonds.svg';
import jackClubs from '../../../assets/cards/jack-clubs.svg';
import jackSpades from '../../../assets/cards/jack-spades.svg';
import queenHearts from '../../../assets/cards/queen-hearts.svg';
import queenDiamonds from '../../../assets/cards/queen-diamonds.svg';
import queenClubs from '../../../assets/cards/queen-clubs.svg';
import queenSpades from '../../../assets/cards/queen-spades.svg';
import kingHearts from '../../../assets/cards/king-hearts.svg';
import kingDiamonds from '../../../assets/cards/king-diamonds.svg';
import kingClubs from '../../../assets/cards/king-clubs.svg';
import kingSpades from '../../../assets/cards/king-spades.svg';
import aceClubs from '../../../assets/cards/AC_face.svg';
import aceSpades from '../../../assets/cards/AS_face.svg';
import aceHearts from '../../../assets/cards/AH_face.svg';
import aceDiamonds from '../../../assets/cards/AD_face.svg';

// ===========================================
// CANVAS CONFIGURATION
// ===========================================

const CANVAS_WIDTH = 1920;
const CANVAS_HEIGHT = 1080;

// Card dimensions (matching current design)
const CARD_WIDTH = 74;
const CARD_HEIGHT = 105;
const CARD_OVERLAP = -56;

// Suit symbols and colors
const SUIT_SYMBOLS: Record<string, string> = {
  hearts: '\u2665',
  diamonds: '\u2666',
  clubs: '\u2663',
  spades: '\u2660',
};

const SUIT_COLORS: Record<string, string> = {
  hearts: '#ef4444',
  diamonds: '#ef4444',
  clubs: '#111827',
  spades: '#111827',
};

// Face card images lookup
const FACE_CARD_IMAGES: Record<string, Record<string, string>> = {
  A: { hearts: aceHearts, diamonds: aceDiamonds, clubs: aceClubs, spades: aceSpades },
  J: { hearts: jackHearts, diamonds: jackDiamonds, clubs: jackClubs, spades: jackSpades },
  Q: { hearts: queenHearts, diamonds: queenDiamonds, clubs: queenClubs, spades: queenSpades },
  K: { hearts: kingHearts, diamonds: kingDiamonds, clubs: kingClubs, spades: kingSpades },
};

// ===========================================
// SEAT POSITIONING (POLAR COORDINATES)
// ===========================================

const TABLE_CENTER = { x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT * 0.20 };

const SEAT_POLAR_POSITIONS = [
  { visualPos: 1, angle: 170, radius: 0.41, rotation: 90 },
  { visualPos: 2, angle: 144, radius: 0.47, rotation: 60 },
  { visualPos: 3, angle: 120, radius: 0.48, rotation: 30 },
  { visualPos: 4, angle: 90, radius: 0.46, rotation: 0 },
  { visualPos: 5, angle: 60, radius: 0.48, rotation: -30 },
  { visualPos: 6, angle: 36, radius: 0.47, rotation: -60 },
  { visualPos: 7, angle: 10, radius: 0.41, rotation: -90 },
];

// Convert polar to cartesian (in canvas pixels)
function polarToCartesian(angle: number, radius: number): { x: number; y: number } {
  const radians = (angle * Math.PI) / 180;
  return {
    x: TABLE_CENTER.x + radius * CANVAS_WIDTH * Math.cos(radians),
    y: TABLE_CENTER.y + radius * CANVAS_WIDTH * Math.sin(radians),
  };
}

// Compute seat positions
const SEAT_POSITIONS = SEAT_POLAR_POSITIONS.map((seat) => {
  const { x, y } = polarToCartesian(seat.angle, seat.radius);
  return { visualPos: seat.visualPos, x, y, rotation: seat.rotation };
});

// Dealer position (top center)
const DEALER_POSITION = { x: CANVAS_WIDTH / 2, y: 130 };

// Shoe position (left of dealer)
const SHOE_POSITION = { x: CANVAS_WIDTH / 2 - 280, y: 80 };

// ===========================================
// TYPES
// ===========================================

interface PlayerState {
  seatPosition: number;
  displayName: string;
  hands: Array<{
    cards: CardType[];
    bet: number;
    outcome?: string;
  }>;
  currentHandIndex: number;
}

interface CanvasGameTableProps {
  // Game state
  dealer: DealerState;
  players: Map<number, PlayerState>;
  mySeatPosition: number | null;
  // Event handlers
  onSeatClick?: (seatPosition: number) => void;
}

// Imperative handle for parent components to trigger animations
export interface CanvasGameTableRef {
  dealCardToSeat: (seatPosition: number, card: CardType, cardIndex: number) => void;
  dealCardToDealer: (card: CardType, cardIndex: number) => void;
}

// Animation state for cards
interface CardAnimation {
  cardKey: string;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  startTime: number;
  duration: number;
  card: CardType;
  onComplete?: () => void;
}

// ===========================================
// ASSET LOADER
// ===========================================

interface LoadedAssets {
  table: HTMLImageElement | null;
  cardBack: HTMLImageElement | null;
  faceCards: Map<string, HTMLImageElement>;
}

function useAssetLoader(): { assets: LoadedAssets; loading: boolean } {
  const [assets, setAssets] = useState<LoadedAssets>({
    table: null,
    cardBack: null,
    faceCards: new Map(),
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadImage = (src: string): Promise<HTMLImageElement> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });
    };

    const loadAllAssets = async () => {
      try {
        // Load table and card back
        const [tableImg, cardBackImg] = await Promise.all([
          loadImage(tableImage),
          loadImage(cardBack),
        ]);

        // Load face card images
        const faceCards = new Map<string, HTMLImageElement>();
        for (const [rank, suits] of Object.entries(FACE_CARD_IMAGES)) {
          for (const [suit, src] of Object.entries(suits)) {
            const key = `${rank}-${suit}`;
            const img = await loadImage(src);
            faceCards.set(key, img);
          }
        }

        setAssets({ table: tableImg, cardBack: cardBackImg, faceCards });
        setLoading(false);
      } catch (error) {
        console.error('[CanvasGameTable] Failed to load assets:', error);
        setLoading(false);
      }
    };

    loadAllAssets();
  }, []);

  return { assets, loading };
}

// ===========================================
// CANVAS DRAWING FUNCTIONS
// ===========================================

function drawTable(ctx: CanvasRenderingContext2D, tableImg: HTMLImageElement) {
  // Center the table image with offset (matching current design)
  const offsetY = -170;
  ctx.drawImage(
    tableImg,
    (CANVAS_WIDTH - tableImg.width) / 2,
    (CANVAS_HEIGHT - tableImg.height) / 2 + offsetY
  );
}

function drawCardBack(
  ctx: CanvasRenderingContext2D,
  cardBackImg: HTMLImageElement,
  x: number,
  y: number,
  rotation: number = 0
) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotation * Math.PI) / 180);

  // Draw card with rounded corners
  ctx.beginPath();
  const radius = 8;
  ctx.roundRect(-CARD_WIDTH / 2, -CARD_HEIGHT / 2, CARD_WIDTH, CARD_HEIGHT, radius);
  ctx.clip();

  // Draw the card back image
  ctx.drawImage(cardBackImg, -CARD_WIDTH / 2, -CARD_HEIGHT / 2, CARD_WIDTH, CARD_HEIGHT);

  ctx.restore();
}

function drawFaceCard(
  ctx: CanvasRenderingContext2D,
  faceCardImg: HTMLImageElement,
  card: CardType,
  x: number,
  y: number,
  rotation: number = 0
) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotation * Math.PI) / 180);

  // White card background with rounded corners
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.roundRect(-CARD_WIDTH / 2, -CARD_HEIGHT / 2, CARD_WIDTH, CARD_HEIGHT, 8);
  ctx.fill();

  // Border
  ctx.strokeStyle = '#d1d5db';
  ctx.lineWidth = 2;
  ctx.stroke();

  // Draw face card image centered
  const imgWidth = 66;
  const imgHeight = 94;
  ctx.drawImage(faceCardImg, -imgWidth / 2, -imgHeight / 2, imgWidth, imgHeight);

  // Draw rank and suit in corners
  const color = SUIT_COLORS[card.suit];
  const symbol = SUIT_SYMBOLS[card.suit];

  ctx.fillStyle = color;
  ctx.font = 'bold 14px sans-serif';
  ctx.textAlign = 'left';

  // Top-left corner
  ctx.fillText(card.rank, -CARD_WIDTH / 2 + 4, -CARD_HEIGHT / 2 + 16);
  ctx.fillText(symbol, -CARD_WIDTH / 2 + 4, -CARD_HEIGHT / 2 + 28);

  // Bottom-right corner (rotated 180)
  ctx.save();
  ctx.rotate(Math.PI);
  ctx.fillText(card.rank, -CARD_WIDTH / 2 + 4, -CARD_HEIGHT / 2 + 16);
  ctx.fillText(symbol, -CARD_WIDTH / 2 + 4, -CARD_HEIGHT / 2 + 28);
  ctx.restore();

  ctx.restore();
}

function drawNumberCard(
  ctx: CanvasRenderingContext2D,
  card: CardType,
  x: number,
  y: number,
  rotation: number = 0
) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotation * Math.PI) / 180);

  // White card background with rounded corners
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.roundRect(-CARD_WIDTH / 2, -CARD_HEIGHT / 2, CARD_WIDTH, CARD_HEIGHT, 8);
  ctx.fill();

  // Border
  ctx.strokeStyle = '#d1d5db';
  ctx.lineWidth = 2;
  ctx.stroke();

  const color = SUIT_COLORS[card.suit];
  const symbol = SUIT_SYMBOLS[card.suit];

  ctx.fillStyle = color;

  // Top-left corner
  ctx.font = 'bold 14px sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText(card.rank, -CARD_WIDTH / 2 + 4, -CARD_HEIGHT / 2 + 16);
  ctx.fillText(symbol, -CARD_WIDTH / 2 + 4, -CARD_HEIGHT / 2 + 28);

  // Center symbol (large)
  ctx.font = '56px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(symbol, 0, -4);

  // Bottom-right corner (rotated 180)
  ctx.save();
  ctx.rotate(Math.PI);
  ctx.font = 'bold 14px sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText(card.rank, -CARD_WIDTH / 2 + 4, -CARD_HEIGHT / 2 + 16);
  ctx.fillText(symbol, -CARD_WIDTH / 2 + 4, -CARD_HEIGHT / 2 + 28);
  ctx.restore();

  ctx.restore();
}

function drawCard(
  ctx: CanvasRenderingContext2D,
  assets: LoadedAssets,
  card: CardType,
  x: number,
  y: number,
  rotation: number = 0
) {
  if (!card.faceUp) {
    if (assets.cardBack) {
      drawCardBack(ctx, assets.cardBack, x, y, rotation);
    }
    return;
  }

  const isFaceCard = ['A', 'J', 'Q', 'K'].includes(card.rank);
  if (isFaceCard) {
    const faceCardImg = assets.faceCards.get(`${card.rank}-${card.suit}`);
    if (faceCardImg) {
      drawFaceCard(ctx, faceCardImg, card, x, y, rotation);
    }
  } else {
    drawNumberCard(ctx, card, x, y, rotation);
  }
}

function drawEmptySeat(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  rotation: number = 0,
  isClickable: boolean = true
) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotation * Math.PI) / 180);

  // Seat box
  const width = 144;
  const height = 140;
  ctx.strokeStyle = isClickable ? 'rgba(107, 114, 128, 0.5)' : 'rgba(107, 114, 128, 0.3)';
  ctx.lineWidth = 2;
  ctx.setLineDash([8, 4]);
  ctx.beginPath();
  ctx.roundRect(-width / 2, -height / 2, width, height, 8);
  ctx.stroke();
  ctx.setLineDash([]);

  // "Join" text
  if (isClickable) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.font = '16px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Join', 0, 0);
  }

  ctx.restore();
}

function drawPlayerSeat(
  ctx: CanvasRenderingContext2D,
  assets: LoadedAssets,
  player: PlayerState,
  x: number,
  y: number,
  rotation: number = 0
) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotation * Math.PI) / 180);

  // Seat box background
  const width = 144;
  const height = 140;
  ctx.fillStyle = 'rgba(22, 101, 52, 0.4)';
  ctx.strokeStyle = 'rgba(107, 114, 128, 0.5)';
  ctx.lineWidth = 2;
  ctx.setLineDash([8, 4]);
  ctx.beginPath();
  ctx.roundRect(-width / 2, -height / 2, width, height, 8);
  ctx.fill();
  ctx.stroke();
  ctx.setLineDash([]);

  // Player name
  ctx.fillStyle = '#ffffff';
  ctx.font = '12px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText(player.displayName, 0, -height / 2 + 4);

  // Draw cards for current hand
  // Default to 0 when currentHandIndex is undefined (e.g., during phase=complete table_state)
  const hand = player.hands[player.currentHandIndex ?? 0];
  if (hand && hand.cards.length > 0) {
    const startX = -(hand.cards.length - 1) * (CARD_WIDTH + CARD_OVERLAP) / 2;
    hand.cards.forEach((card, index) => {
      const cardX = startX + index * (CARD_WIDTH + CARD_OVERLAP);
      drawCard(ctx, assets, card, cardX, 0, 0);
    });
  }

  // Score display
  if (hand && hand.cards.length > 0) {
    ctx.fillStyle = '#1f2937';
    ctx.beginPath();
    ctx.roundRect(-width / 2, height / 2 - 20, 40, 20, [0, 8, 0, 0]);
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 14px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // Calculate score (simplified)
    let score = 0;
    let aces = 0;
    for (const card of hand.cards) {
      if (!card.faceUp) continue;
      if (card.rank === 'A') {
        aces++;
        score += 11;
      } else if (['K', 'Q', 'J'].includes(card.rank)) {
        score += 10;
      } else {
        score += parseInt(card.rank);
      }
    }
    while (score > 21 && aces > 0) {
      score -= 10;
      aces--;
    }
    ctx.fillText(score.toString(), -width / 2 + 20, height / 2 - 10);
  }

  ctx.restore();
}

function drawDealerArea(
  ctx: CanvasRenderingContext2D,
  assets: LoadedAssets,
  dealer: DealerState,
  x: number,
  y: number
) {
  ctx.save();
  ctx.translate(x, y);

  // Dealer seat box
  const width = 144;
  const height = 140;
  ctx.fillStyle = 'rgba(22, 101, 52, 0.4)';
  ctx.strokeStyle = 'rgba(107, 114, 128, 0.5)';
  ctx.lineWidth = 2;
  ctx.setLineDash([8, 4]);
  ctx.beginPath();
  ctx.roundRect(-width / 2, -height / 2, width, height, 8);
  ctx.fill();
  ctx.stroke();
  ctx.setLineDash([]);

  // Dealer name
  ctx.fillStyle = '#ffffff';
  ctx.font = '12px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText('Dealer', 0, -height / 2 + 4);

  // Draw dealer cards
  if (dealer.cards.length > 0) {
    const startX = -(dealer.cards.length - 1) * (CARD_WIDTH + CARD_OVERLAP) / 2;
    dealer.cards.forEach((card, index) => {
      const cardX = startX + index * (CARD_WIDTH + CARD_OVERLAP);
      drawCard(ctx, assets, card, cardX, 0, 0);
    });

    // Score display
    ctx.fillStyle = '#1f2937';
    ctx.beginPath();
    ctx.roundRect(-width / 2, height / 2 - 20, 40, 20, [0, 8, 0, 0]);
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 14px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(dealer.visibleScore.toString(), -width / 2 + 20, height / 2 - 10);
  } else {
    // Empty placeholder
    ctx.strokeStyle = 'rgba(107, 114, 128, 0.3)';
    ctx.lineWidth = 2;
    ctx.setLineDash([8, 4]);
    ctx.beginPath();
    ctx.roundRect(-CARD_WIDTH / 2, -CARD_HEIGHT / 2, CARD_WIDTH, CARD_HEIGHT, 8);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  ctx.restore();
}

// ===========================================
// COMPONENT
// ===========================================

export const CanvasGameTable = forwardRef<CanvasGameTableRef, CanvasGameTableProps>(function CanvasGameTable({
  dealer,
  players,
  mySeatPosition,
  onSeatClick,
}, ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { assets, loading } = useAssetLoader();
  const [scale, setScale] = useState(1);
  const animationsRef = useRef<CardAnimation[]>([]);
  const animationFrameRef = useRef<number>();
  const renderRef = useRef<() => void>();

  // Calculate viewport scale
  useEffect(() => {
    const calculateScale = () => {
      const HEADER_HEIGHT = 56;
      const viewportWidth = window.innerWidth;
      const availableHeight = window.innerHeight - HEADER_HEIGHT;
      const scaleX = viewportWidth / CANVAS_WIDTH;
      const scaleY = availableHeight / CANVAS_HEIGHT;
      setScale(Math.min(scaleX, scaleY));
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, []);

  // Get visual position for a seat (handles rotation when player joins)
  const getVisualPositionForSeat = useCallback((actualSeat: number): number => {
    if (mySeatPosition === null) return actualSeat;
    return ((actualSeat - mySeatPosition + 4 - 1 + 7) % 7) + 1;
  }, [mySeatPosition]);

  // Main render function
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || loading) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.fillStyle = '#1a4731';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Draw table background
    if (assets.table) {
      drawTable(ctx, assets.table);
    }

    // Draw dealer area
    drawDealerArea(ctx, assets, dealer, DEALER_POSITION.x, DEALER_POSITION.y);

    // Draw player seats
    for (let actualSeat = 1; actualSeat <= 7; actualSeat++) {
      const visualPos = getVisualPositionForSeat(actualSeat);
      const pos = SEAT_POSITIONS[visualPos - 1];
      const player = players.get(actualSeat);

      if (player) {
        drawPlayerSeat(ctx, assets, player, pos.x, pos.y, pos.rotation);
      } else {
        drawEmptySeat(ctx, pos.x, pos.y, pos.rotation, mySeatPosition === null);
      }
    }

    // Draw animations
    const now = performance.now();
    const activeAnimations: CardAnimation[] = [];

    for (const anim of animationsRef.current) {
      const elapsed = now - anim.startTime;
      const progress = Math.min(elapsed / anim.duration, 1);

      // Easing function (ease-out cubic)
      const eased = 1 - Math.pow(1 - progress, 3);

      const x = anim.startX + (anim.endX - anim.startX) * eased;
      const y = anim.startY + (anim.endY - anim.startY) * eased;

      drawCard(ctx, assets, anim.card, x, y, 0);

      if (progress < 1) {
        activeAnimations.push(anim);
      } else if (anim.onComplete) {
        anim.onComplete();
      }
    }

    animationsRef.current = activeAnimations;

    // Continue animation loop if there are active animations
    if (activeAnimations.length > 0) {
      animationFrameRef.current = requestAnimationFrame(render);
    }
  }, [assets, loading, dealer, players, mySeatPosition, getVisualPositionForSeat]);

  // Store render function in ref for imperative access
  renderRef.current = render;

  // Render on state changes
  useEffect(() => {
    render();
  }, [render]);

  // Expose imperative methods for triggering animations
  useImperativeHandle(ref, () => ({
    dealCardToSeat: (seatPosition: number, card: CardType, cardIndex: number) => {
      const visualPos = getVisualPositionForSeat(seatPosition);
      const pos = SEAT_POSITIONS[visualPos - 1];
      const endX = pos.x + cardIndex * (CARD_WIDTH + CARD_OVERLAP);
      const endY = pos.y;

      const animation: CardAnimation = {
        cardKey: `deal-${seatPosition}-${cardIndex}-${Date.now()}`,
        startX: SHOE_POSITION.x,
        startY: SHOE_POSITION.y,
        endX,
        endY,
        startTime: performance.now(),
        duration: 300,
        card,
      };

      animationsRef.current.push(animation);
      if (animationFrameRef.current === undefined) {
        animationFrameRef.current = requestAnimationFrame(() => {
          animationFrameRef.current = undefined;
          renderRef.current?.();
        });
      }
    },
    dealCardToDealer: (card: CardType, cardIndex: number) => {
      const endX = DEALER_POSITION.x + cardIndex * (CARD_WIDTH + CARD_OVERLAP);
      const endY = DEALER_POSITION.y;

      const animation: CardAnimation = {
        cardKey: `deal-dealer-${cardIndex}-${Date.now()}`,
        startX: SHOE_POSITION.x,
        startY: SHOE_POSITION.y,
        endX,
        endY,
        startTime: performance.now(),
        duration: 300,
        card,
      };

      animationsRef.current.push(animation);
      if (animationFrameRef.current === undefined) {
        animationFrameRef.current = requestAnimationFrame(() => {
          animationFrameRef.current = undefined;
          renderRef.current?.();
        });
      }
    },
  }), [getVisualPositionForSeat]);

  // Handle canvas click (for seat selection)
  const handleClick = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onSeatClick || mySeatPosition !== null) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) / scale;
    const y = (event.clientY - rect.top) / scale;

    // Check if click is within any empty seat
    for (let actualSeat = 1; actualSeat <= 7; actualSeat++) {
      if (players.has(actualSeat)) continue;

      const visualPos = getVisualPositionForSeat(actualSeat);
      const pos = SEAT_POSITIONS[visualPos - 1];

      // Simple rectangular hit test (ignoring rotation for now)
      const seatWidth = 144;
      const seatHeight = 140;
      if (
        x >= pos.x - seatWidth / 2 &&
        x <= pos.x + seatWidth / 2 &&
        y >= pos.y - seatHeight / 2 &&
        y <= pos.y + seatHeight / 2
      ) {
        onSeatClick(actualSeat);
        break;
      }
    }
  }, [scale, players, mySeatPosition, getVisualPositionForSeat, onSeatClick]);

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="w-screen h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading game assets...</div>
      </div>
    );
  }

  return (
    <div className="w-screen h-screen bg-gray-900 overflow-hidden flex flex-col">
      {/* Header placeholder - would match existing header */}
      <div className="flex-shrink-0 h-14 bg-gray-900 border-b border-gray-700 flex items-center justify-center">
        <div className="text-white text-lg">Canvas Prototype</div>
      </div>

      {/* Canvas container */}
      <div className="flex-1 overflow-hidden flex items-center justify-center">
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          onClick={handleClick}
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'center center',
            cursor: mySeatPosition === null ? 'pointer' : 'default',
          }}
        />
      </div>
    </div>
  );
});

export default CanvasGameTable;
